import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { NewMatrixCriterion } from '../../../../types/advise';
import { MATRIX_DESCRIPTION_LIMIT, MATRIX_NAME_LIMIT, MATRIX_NEW_FEATURES_LIMIT } from './matrixLimits';

export interface AddCustomCategoryDialogProps {
  onClose: () => void;
  // Resolves to why it was refused, or null once added.
  onCreate: (criterion: NewMatrixCriterion) => Promise<string | null>;
}

/** Adds a custom criterion; mount it only while open, so every opening starts empty. */
export const AddCustomCategoryDialog = ({ onClose, onCreate }: AddCustomCategoryDialogProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [withFeatures, setWithFeatures] = useState(true);
  const [features, setFeatures] = useState(['']);
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const setFeature = (index: number, value: string) =>
    setFeatures((rows) => rows.map((row, at) => (at === index ? value : row)));

  const create = async () => {
    setSaving(true);
    const refused = await onCreate({
      label: name.trim(),
      description: description.trim() || null,
      features: withFeatures ? features.map((row) => row.trim()).filter(Boolean) : []
    });
    setSaving(false);
    if (refused) setRefusal(refused);
    else onClose();
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="xs"
      onClose={saving ? undefined : onClose}>
      <DialogTitle className="flex items-center justify-between">
        Add Custom Category
        <IconButton
          aria-label="Close"
          size="small"
          disabled={saving}
          onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        className="flex flex-col gap-4">
        {refusal && <Alert severity="error">{refusal}</Alert>}
        <TextField
          label="Category Name"
          placeholder="e.g. OKR Tracking"
          size="small"
          required
          disabled={saving}
          value={name}
          inputProps={{ maxLength: MATRIX_NAME_LIMIT }}
          onChange={(event) => setName(event.target.value)}
        />
        <TextField
          label="Description (optional)"
          placeholder="Short description shown under the category header"
          size="small"
          disabled={saving}
          value={description}
          inputProps={{ maxLength: MATRIX_DESCRIPTION_LIMIT }}
          onChange={(event) => setDescription(event.target.value)}
        />
        <Box className="flex flex-col gap-2 rounded border border-grey-2 bg-grey-1 p-3">
          <FormControlLabel
            label="Add custom features to this category now"
            control={
              <Checkbox
                size="small"
                disabled={saving}
                checked={withFeatures}
                onChange={(event) => setWithFeatures(event.target.checked)}
              />
            }
            slotProps={{ typography: { fontSize: 14, fontWeight: 600 } }}
          />
          {withFeatures && (
            <>
              {features.map((feature, index) => (
                <Box
                  // Rows have no identity beyond their place, and removing one re-renders the rest from their values.
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className="flex items-center gap-2">
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Feature name…"
                    disabled={saving}
                    value={feature}
                    inputProps={{ 'aria-label': `Feature ${index + 1}`, maxLength: MATRIX_NAME_LIMIT }}
                    onChange={(event) => setFeature(index, event.target.value)}
                  />
                  <IconButton
                    aria-label={`Remove feature ${index + 1}`}
                    size="small"
                    disabled={saving}
                    onClick={() => setFeatures((rows) => rows.filter((_, at) => at !== index))}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              {features.length < MATRIX_NEW_FEATURES_LIMIT && (
                <Box>
                  <Button
                    size="small"
                    disabled={saving}
                    onClick={() => setFeatures((rows) => [...rows, ''])}>
                    + Add another feature
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={saving}
          onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving || !name.trim()}
          onClick={create}>
          Create Category
        </Button>
      </DialogActions>
    </Dialog>
  );
};
